import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { 
  XMarkIcon,
  CheckIcon,
  CubeIcon,
  TagIcon,
  CurrencyEuroIcon,
  PencilIcon
} from '@heroicons/react/24/outline'
import { 
  UPDATE_BUNDLE_GROUP, 
  CREATE_BUNDLE_GROUP_OPTION, 
  UPDATE_BUNDLE_GROUP_OPTION, 
  DELETE_BUNDLE_GROUP_OPTION 
} from '../graphql/mutations'
import LoadingSpinner from './LoadingSpinner'
import toast from 'react-hot-toast'

interface EditMealPartModalProps {
  group: any
  allProducts: any[]
  productsLoading: boolean
  onClose: () => void
  onSave: (updatedGroup: any) => void
}

const EditMealPartModal = ({ 
  group, 
  allProducts, 
  productsLoading, 
  onClose, 
  onSave 
}: EditMealPartModalProps) => {
  const [selectedVariants, setSelectedVariants] = useState<Set<number>>(new Set())
  const [priceDeltas, setPriceDeltas] = useState<Record<number, number>>({})
  const [groupName, setGroupName] = useState('')
  const [requiredCount, setRequiredCount] = useState(1)
  const [savingGroupInfo, setSavingGroupInfo] = useState(false)
  const [savingOptions, setSavingOptions] = useState(false)
  const [editingGroupInfo, setEditingGroupInfo] = useState(false)
  const [originalGroupName, setOriginalGroupName] = useState('')
  const [originalRequiredCount, setOriginalRequiredCount] = useState(1)
  const [originalSelectedVariants, setOriginalSelectedVariants] = useState<Set<number>>(new Set())
  const [originalPriceDeltas, setOriginalPriceDeltas] = useState<Record<number, number>>({})

  // Mutation hooks
  const [updateBundleGroup] = useMutation(UPDATE_BUNDLE_GROUP)
  const [createBundleGroupOption] = useMutation(CREATE_BUNDLE_GROUP_OPTION)
  const [updateBundleGroupOption] = useMutation(UPDATE_BUNDLE_GROUP_OPTION)
  const [deleteBundleGroupOption] = useMutation(DELETE_BUNDLE_GROUP_OPTION)

  useEffect(() => {
    if (group) {
      setGroupName(group.name)
      setRequiredCount(group.required_count)
      setOriginalGroupName(group.name)
      setOriginalRequiredCount(group.required_count)
      
      // Initialize selected variants from existing options
      const existingVariants = new Set<number>()
      const existingPriceDeltas: Record<number, number> = {}
      
      group.options?.forEach((option: any) => {
        existingVariants.add(option.eligible_variant.id)
        existingPriceDeltas[option.eligible_variant.id] = option.price_delta_cents
      })
      
      setSelectedVariants(existingVariants)
      setPriceDeltas(existingPriceDeltas)
      setOriginalSelectedVariants(new Set(existingVariants))
      setOriginalPriceDeltas({ ...existingPriceDeltas })
    }
  }, [group])

  const handleVariantToggle = (variantId: number) => {
    const newSelected = new Set(selectedVariants)
    if (newSelected.has(variantId)) {
      newSelected.delete(variantId)
      // Remove price delta when unselecting
      const newPriceDeltas = { ...priceDeltas }
      delete newPriceDeltas[variantId]
      setPriceDeltas(newPriceDeltas)
    } else {
      newSelected.add(variantId)
      // Set default price delta to 0 when selecting
      setPriceDeltas(prev => ({ ...prev, [variantId]: 0 }))
    }
    setSelectedVariants(newSelected)
  }

  const handlePriceDeltaChange = (variantId: number, value: string) => {
    const cents = Math.round(parseFloat(value || '0') * 100)
    setPriceDeltas(prev => ({ ...prev, [variantId]: cents }))
  }

  const formatPrice = (priceCents: number) => {
    return `€${(priceCents / 100).toFixed(2)}`
  }

  // removed unused formatPriceDelta

  const hasGroupInfoChanges = () => {
    return groupName !== originalGroupName || requiredCount !== originalRequiredCount
  }

  const hasOptionsChanges = () => {
    // Check if selected variants changed
    if (selectedVariants.size !== originalSelectedVariants.size) return true
    
    for (const variantId of selectedVariants) {
      if (!originalSelectedVariants.has(variantId)) return true
    }
    
    for (const variantId of originalSelectedVariants) {
      if (!selectedVariants.has(variantId)) return true
    }
    
    // Check if price deltas changed for existing variants
    for (const variantId of selectedVariants) {
      const currentDelta = priceDeltas[variantId] || 0
      const originalDelta = originalPriceDeltas[variantId] || 0
      if (currentDelta !== originalDelta) return true
    }
    
    return false
  }

  const handleEditGroupInfo = () => {
    setEditingGroupInfo(true)
  }

  const handleCancelEditGroupInfo = () => {
    setGroupName(originalGroupName)
    setRequiredCount(originalRequiredCount)
    setEditingGroupInfo(false)
  }

  const updateBundleGroupInfo = async () => {
    setSavingGroupInfo(true)
    try {
      // Update the bundle group basic info (name, required_count, sort_order)
      await updateBundleGroup({
        variables: {
          id: Number(group.id),
          bundleGroupData: {
            bundle_product_id: Number(group.bundle_product.id),
            name: groupName,
            required_count: Number(requiredCount),
            sort_order: group.sort_order || 10
          }
        }
      })
      toast.success('Group settings updated successfully')
      setOriginalGroupName(groupName)
      setOriginalRequiredCount(requiredCount)
      setEditingGroupInfo(false)
      onSave({
        ...group,
        name: groupName,
        required_count: requiredCount
      })
    } catch (error: any) {
      toast.error(`Failed to update group settings: ${error.message}`)
    } finally {
      setSavingGroupInfo(false)
    }
  }

  const updateBundleGroupOptions = async () => {
    setSavingOptions(true)
    try {
      // Get current options for comparison
      const currentOptions = group.options || []
      const currentVariantIds = new Set(currentOptions.map((opt: any) => opt.eligible_variant.id))
      const newVariantIds = new Set(selectedVariants)

      // 1. Delete options that are no longer selected
      const optionsToDelete = currentOptions.filter((opt: any) => 
        !newVariantIds.has(opt.eligible_variant.id)
      )
      
      for (const option of optionsToDelete) {
        await deleteBundleGroupOption({
          variables: { id: Number(option.id) }
        })
      }

      // 2. Update existing options that are still selected
      const optionsToUpdate = currentOptions.filter((opt: any) => 
        newVariantIds.has(opt.eligible_variant.id)
      )
      
      for (const option of optionsToUpdate) {
        const variantId = option.eligible_variant.id
        const newPriceDelta = priceDeltas[variantId] || 0
        
        // Only update if price delta changed
        if (option.price_delta_cents !== newPriceDelta) {
          await updateBundleGroupOption({
            variables: {
              id: Number(option.id),
              optionData: {
                price_delta_cents: newPriceDelta,
                sort_order: option.sort_order || 10
              }
            }
          })
        }
      }

      // 3. Create new options for newly selected variants
      const variantsToCreate = Array.from(newVariantIds).filter(variantId => 
        !currentVariantIds.has(variantId)
      )
      
      for (const variantId of variantsToCreate) {
        await createBundleGroupOption({
          variables: {
            optionData: {
              bundle_group_id: Number(group.id),
              eligible_variant_id: Number(variantId),
              price_delta_cents: priceDeltas[variantId] || 0,
              sort_order: 10
            }
          }
        })
      }
      
      toast.success('Available items updated successfully')
      setOriginalSelectedVariants(new Set(selectedVariants))
      setOriginalPriceDeltas({ ...priceDeltas })
      onSave({
        ...group,
        name: groupName,
        required_count: requiredCount
      })
    } catch (error: any) {
      toast.error(`Failed to update available items: ${error.message}`)
    } finally {
      setSavingOptions(false)
    }
  }


  const isVariantSelected = (variantId: number) => selectedVariants.has(variantId)

  if (productsLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-medium text-gray-900">Edit Meal Part: {group.name}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Group Settings */}
            <div className="lg:col-span-1 space-y-6">
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Group Settings</h4>
                  {!editingGroupInfo && (
                    <button
                      onClick={handleEditGroupInfo}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Edit group settings"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                {editingGroupInfo ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Part Name
                      </label>
                      <input
                        type="text"
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Required Selections
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={requiredCount}
                        onChange={(e) => setRequiredCount(Number(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        How many items must be selected from this part
                      </p>
                    </div>
                    
                    <div className="flex space-x-2 pt-2">
                      <button
                        onClick={updateBundleGroupInfo}
                        disabled={savingGroupInfo || !hasGroupInfoChanges()}
                        className={`px-3 py-1 text-sm font-medium text-white rounded-md ${
                          savingGroupInfo || !hasGroupInfoChanges()
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {savingGroupInfo ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEditGroupInfo}
                        disabled={savingGroupInfo}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Part Name
                      </label>
                      <p className="text-sm text-gray-900">{groupName}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        Required Selections
                      </label>
                      <p className="text-sm text-gray-900">
                        {requiredCount} item{requiredCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Selection Summary */}
              <div className="card">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Selection Summary</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Selected Variants</span>
                    <span className="text-sm font-medium text-gray-900">{selectedVariants.size}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Unique Products</span>
                    <span className="text-sm font-medium text-gray-900">
                      {(() => {
                        const selectedProducts = new Set<string>()
                        selectedVariants.forEach((variantId: number) => {
                          const variant = allProducts
                            .flatMap((p: any) => p.variants)
                            .find((v: any) => v.id === variantId)
                          if (variant) {
                            const product = allProducts.find((p: any) => 
                              p.variants.some((v: any) => v.id === variantId)
                            )
                            if (product) selectedProducts.add(product.name)
                          }
                        })
                        return selectedProducts.size
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Product/Variant Selection */}
            <div className="lg:col-span-2">
              <div className="card">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Select Available Items</h4>
                
                {allProducts.length > 0 ? (
                  <div className="space-y-6 max-h-96 overflow-y-auto">
                    {allProducts.map((product) => (
                      <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <CubeIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <h5 className="text-sm font-medium text-gray-900">{product.name}</h5>
                              <p className="text-xs text-gray-500">
                                {product.variants?.length || 0} variant{(product.variants?.length || 0) !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            product.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {product.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>

                        {product.variants && product.variants.length > 0 ? (
                          <div className="space-y-2">
                            {product.variants.map((variant: any) => (
                              <div key={variant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => handleVariantToggle(variant.id)}
                                    className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      isVariantSelected(variant.id)
                                        ? 'bg-primary-600 border-primary-600 text-white'
                                        : 'border-gray-300 hover:border-primary-500'
                                    }`}
                                  >
                                    {isVariantSelected(variant.id) && (
                                      <CheckIcon className="h-3 w-3" />
                                    )}
                                  </button>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <TagIcon className="h-4 w-4 text-gray-400" />
                                      <span className="text-sm font-medium text-gray-900">
                                        {variant.name}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                                      <span>Base: {formatPrice(variant.price_cents)}</span>
                                      {variant.sku && <span>SKU: {variant.sku}</span>}
                                      <span className={`px-1 py-0.5 rounded text-xs ${
                                        variant.active 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {variant.active ? 'Active' : 'Inactive'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                {isVariantSelected(variant.id) && (
                                  <div className="flex items-center space-x-2">
                                    <div className="flex items-center space-x-1">
                                      <CurrencyEuroIcon className="h-4 w-4 text-gray-400" />
                                      <input
                                        type="number"
                                        step="0.01"
                                        value={((priceDeltas[variant.id] || 0) / 100).toFixed(2)}
                                        onChange={(e) => handlePriceDeltaChange(variant.id, e.target.value)}
                                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        placeholder="0.00"
                                      />
                                    </div>
                                    <span className="text-xs text-gray-500">price adjustment</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-sm text-gray-500">No variants available</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No products available</h3>
                    <p className="mt-1 text-sm text-gray-500">No active products found to add to this meal part.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="pt-6 border-t border-gray-200 mt-6">
            <div className="flex justify-between items-center">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              
              <button
                onClick={updateBundleGroupOptions}
                disabled={savingOptions || !hasOptionsChanges()}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  savingOptions || !hasOptionsChanges()
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {savingOptions ? 'Saving...' : 'Save Available Items'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditMealPartModal
